/**
 * types-bridge — pure re-exports of generated OpenAPI schemas under
 * stable, FE-friendly names.
 *
 * As of the post-Phase-2d codegen (2026-04-29), every type in this file
 * is a one-line alias of `components['schemas'][...]`. There are no
 * hand-written interfaces. This file exists for two reasons:
 *
 *   1. Names like `Me`, `BoardResponse`, `Comment`, `Project`, etc. read
 *      better in feature code than `components['schemas']['MeData']`.
 *   2. A couple of FE-side names diverge from the generated schema names
 *      so the FE can keep using the unwrapped payload type (`Me`,
 *      `BoardResponse`) while the generated schema reflects the BE's
 *      `{ data: ... }` envelope. See the alias mapping below.
 *
 * NOTE: this file deliberately does NOT add hand-written types anymore.
 * If you need a new type, add it to a BE Zod schema, redeploy, run
 * `pnpm openapi:codegen`, and re-export from here. The migration shim is
 * over.
 */

import type { components } from '@/api/generated/api'

type Schemas = components['schemas']

// ---------------------------------------------------------------------------
// Phase 1 — auth + identity.
// ---------------------------------------------------------------------------

export type UserKind = Schemas['UserKind']
export type TenantPlan = Schemas['TenantPlan']

/**
 * Phase 3.9 — three FE themes. The slug is what the BE persists on
 * `users.theme` and what the FE applies as `<html data-theme>`.
 */
export const THEMES = ['default', 'obsidian', 'midnight'] as const
export type Theme = (typeof THEMES)[number]

/**
 * Supported UI locales — the slug is what the BE persists on
 * `users.locale` and what the FE applies as `<html lang>` + i18next's
 * active language. The pseudo-locale `en-XA` is dev-only (generated
 * client-side) and intentionally NOT in this set — only real locales
 * are persistable.
 */
export const LOCALES = ['en', 'es'] as const
export type Locale = (typeof LOCALES)[number]

/**
 * Per-user "first day of the week" preference. Drives the column
 * order of every week-based view + "this week" / "last week" /
 * "next week" range math. Per-user (not per-tenant) because regional
 * conventions vary across a single org. The signup path seeds a
 * smart default from `Accept-Language`; this is the user's explicit
 * override.
 */
export const FIRST_DAYS_OF_WEEK = ['monday', 'sunday', 'saturday'] as const
export type FirstDayOfWeek = (typeof FIRST_DAYS_OF_WEEK)[number]

/**
 * Phase 3.9: `theme` is added to the BE's User schema but won't appear
 * in `Schemas['User']` until the next `pnpm openapi:codegen` run lands.
 * Override here so consumers can read `me.user.theme` immediately.
 * i18n initiative adds `locale` on the same shape — keep both optional
 * until the codegen catches up. Drop the override once codegen catches
 * up.
 */
export type MeUser = Schemas['User'] & {
  /** IANA zone from the profile; null/absent = use the browser's. */
  readonly timezone?: string | null
  readonly theme?: Theme
  readonly locale?: Locale
  /**
   * Per-user first-day-of-week preference. Optional on the wire until
   * codegen catches up; consumers fall back to `'monday'` (ISO).
   */
  readonly firstDayOfWeek?: FirstDayOfWeek
  /**
   * Phase 2 — browser notification preferences. Optional on the
   * wire until the BE me-route extension lands and `pnpm openapi:codegen`
   * absorbs the new fields. Consumers fall back to `false` / `null`.
   */
  readonly browserNotificationsEnabled?: boolean
  readonly notificationPromptDismissedAt?: string | null
}

/**
 * Mirrors the BE's `master_board_layout` enum. `masterBoardLayout`
 * / `masterBoardCompact` are already required fields on the generated
 * `User` schema (staging deployed them ahead of this FE PR), so this is a
 * pure re-export rather than a `MeUser` widening — no override needed.
 * `src/features/dashboard/master-layout.ts` keeps its own runtime tuple
 * (`MASTER_LAYOUTS`, used for iteration + membership checks) and asserts
 * it structurally matches this type at compile time.
 */
export type MasterBoardLayout = Schemas['MasterBoardLayout']
export type MeTenant = Schemas['Tenant'] & { readonly defaultLocale?: Locale }
/**
 * The caller's resolved RBAC permission slugs, serialized from the same
 * set the BE gates its routes on. Optional on the wire until
 * `pnpm openapi:codegen` absorbs it — consumers must treat an ABSENT
 * array as "allow" (an older BE that doesn't send the field) and an
 * EMPTY array as "deny". Drop the override once codegen catches up.
 */
export type MeMembership = Omit<Schemas['Membership'], 'role'> & {
  readonly role: TenantRoleSlug
  readonly permissions?: readonly string[]
}

/**
 * Tenant role slugs, widened past the generated `Role` union to carry
 * `'guest'`.
 *
 * The generated union comes from the BE's OpenAPI contract, and the
 * guest rung lands in the paired BE PR — until that pipeline is green and
 * `pnpm openapi:codegen` reruns, a `role === 'guest'` comparison against
 * the narrow union is a compile error ("no overlap"), not a lint nit.
 * Collapse this back to `Schemas['Role']` once codegen carries the rung.
 *
 * The union is the only place a role NAME is legitimate: capability
 * gating reads `membership.permissions` slugs, because a tenant admin can
 * edit any system role's permission set. Guest-ness is the exception —
 * the slug IS the ceiling, and no permission edit can widen it — so
 * identity checks against `'guest'` are correct where capability checks
 * against a role name never are.
 */
export type TenantRoleSlug = Schemas['Role'] | 'guest'

/**
 * Unwrapped `/api/me` payload. The generated `MeResponse` is the BE's
 * `{ data: MeData }` envelope; FE consumers unwrap inside `useMe`'s
 * `queryFn`, so the cached value matches `MeData`.
 *
 * Phase 3.9 override widens `user` to carry the optional `theme` slug
 * the BE now returns; `membership` carries the caller's permission
 * slugs on the same terms.
 */
export type Me = Omit<Schemas['MeData'], 'user' | 'membership'> & {
  user: MeUser
  membership: MeMembership
  /**
   * Workspace feature switches. Optional on the wire until codegen
   * carries it; an ABSENT block reads as every feature off, so an older
   * BE never surfaces UI for something it cannot serve.
   */
  features?: MeFeatures
}

export interface MeFeatures {
  /** AI agent seats are enabled for this deployment. */
  readonly agents: boolean
  /**
   * Agents may point at a model endpoint outside the listed public
   * providers (Premium). Optional until the BE sends it; absent reads as
   * false, so an older BE keeps Custom locked rather than offering it.
   */
  readonly privateModelEndpoints?: boolean
  /**
   * Agent workstations (a computer an agent can use for longer work) are
   * available. Optional until the BE sends it; absent reads as false.
   */
  readonly workstations?: boolean
}

export type BootstrapInput = Schemas['BootstrapUserInput']

// ---------------------------------------------------------------------------
// Phase 2a / 2b — projects + board.
// ---------------------------------------------------------------------------

export type DeliverableKind = Schemas['DeliverableKind']

/**
 * Slim project view used everywhere in the FE. The full
 * `Schemas['Project']` adds `tenantId`, which the FE never reads;
 * meanwhile `Schemas['BoardData']['project']` is a slim shape without
 * `tenantId`. Aliasing to the slim shape lets `BoardPage` fall back to
 * `[board.data.project]` for the project switcher without a structural
 * mismatch against `useProjects()`'s list (which is wider on the wire
 * but assignable to the slim type).
 *
 * Wave 2 Batch B (Phase 3.5) adds `archivedAt: string | null` and
 * `code` to the slim shape (legacy `Project.inactive` + the per-tenant
 * project code prefix). Both are marked optional here so the FE
 * compiles + ships before the BE codegen lands; consumers must gate
 * on truthiness (`Boolean(project.archivedAt)`) or fallback (`project.code
 * ?? ''`). Once `pnpm openapi:codegen` runs against a BE that includes
 * the fields, this override collapses back to a one-line re-export.
 */
export type Project = Omit<
  Schemas['BoardData']['project'],
  'repositoryCount' | 'onlyRepository'
> & {
  /**
   * Repositories linked to the project (still available). Present on the
   * board payload's project; the list route may omit it, so optional.
   */
  readonly repositoryCount?: number
  /**
   * The project's only linked repository (when `repositoryCount` is 1), else
   * `null`. The generated type drops `nullable`, so it is re-declared here.
   */
  readonly onlyRepository?: CardRepository | null
  readonly archivedAt?: string | null
  readonly code?: string
  /** Optional client this project bills against. */
  readonly clientId?: string | null
  /**
   * Done-column visibility override in days. `0` = never hide, `null` =
   * inherit the organization default. Optional until codegen absorbs
   * the new BE column; consumers treat `undefined` like `null`.
   */
  readonly doneVisibleDays?: number | null
}
/**
 * Project status / column on the Kanban board.
 *
 * `allowsTimer` is a Phase 3c-2 BE addition (every status carries
 * whether a timer can be started for cards in it — true for Today and
 * Review, false for Inbox/Up Next/Done). It's marked optional here so
 * the FE compiles + ships before the BE codegen lands; consumers must
 * gate on truthiness so an `undefined` (pre-BE-deploy) falls open and
 * the start button stays enabled. Once `pnpm openapi:codegen` runs
 * against a BE that includes the field, this override collapses to a
 * one-line re-export of the generated schema.
 */
export type ProjectStatus = Schemas['ProjectStatus'] & {
  readonly allowsTimer?: boolean
}

/**
 * Which of the five canonical board columns a status behaves as. Column
 * identity is the TYPE, not the name — a project's custom `Testing`
 * column typed `review` buckets under Review on the master board, and
 * survives a rename that the old name-equality checks did not.
 *
 * Derived from the generated schema so the union can never drift from
 * the BE enum.
 */
export type ProjectStatusType = Schemas['ProjectStatus']['columnType']

export type ProjectMember = Schemas['ProjectMember']

/**
 * Single discriminated card type across all four `DeliverableKind`s.
 *
 * Phase 3c-2: `shorthand?: string | null` already lives on the unified
 * `BoardCard` schema (see `3mote-be-new/src/schemas/board.ts`). It's
 * populated for tickets today and — once BE-2 of Phase 3c-2 deploys +
 * backfills — for tasks and events too. Announcements stay without (no
 * shorthand on broadcasts). The FE renders the pill conditionally so
 * missing values are silent; see `<CardShorthand>` in
 * `features/board/components/BoardCard.tsx`.
 */
/**
 * Wave 3 Batch J extension — Event/Announcement gap-fill fields. Marked
 * optional because:
 *   - `attendees` only ships on `kind === 'event'` and `'announcement'`
 *   - `collaborators` and `dates` only ship on `kind === 'announcement'`
 *   - `timezone` ships on both (NULL = use viewer's local TZ)
 *
 * Wave 4 Batch K — events + announcements unify on `attendees` and drop
 * the single-assignee model. The BE's `BoardCard` now ships
 * `assigneeId/assigneeName/assigneeAvatarUrl` as OPTIONAL (present on
 * tasks/tickets, absent on events/announcements) and adds
 * `attendees: BoardCardAttendee[]` (with avatarUrl) on events +
 * announcements. Until the next OpenAPI codegen lands, this override
 * widens the assignee fields to optional and adds the attendee shape.
 */
export interface BoardCardUserRef {
  readonly userId: string
  readonly name: string
  readonly email: string
  /** Human vs AI agent — drives the `<AiTag>`; absent on a pre-`kind` payload. */
  readonly kind?: UserKind
}

/**
 * Wave 4 Batch K — attendee shape on event + announcement cards. Carries
 * an avatar URL (absolute CDN per ADR-011) so the card face can render
 * the avatar group without a per-user lookup.
 */
export interface BoardCardAttendee {
  readonly userId: string
  readonly name: string
  readonly avatarUrl: string | null
  readonly kind?: UserKind
}

export interface AnnouncementDateRow {
  readonly id: string
  readonly entryType: 'time' | 'duration'
  readonly date: string
  readonly startTime: string | null
  readonly endTime: string | null
  readonly hours: number | null
  readonly minutes: number | null
  readonly position: number
}

/**
 * Recurrence rule frequencies, weekdays, ordinals, and trigger types.
 * Source of truth: `3mote-be-new/src/schemas/recurrences.ts`. These
 * collapse to one-line re-exports of `Schemas['Recurrence*']` once the
 * Phase 3 BE deploys and `pnpm openapi:codegen` picks them up.
 */
export const RECURRENCE_FREQUENCIES = ['daily', 'weekly', 'monthly', 'yearly'] as const
export type RecurrenceFrequency = (typeof RECURRENCE_FREQUENCIES)[number]

export const RECURRENCE_WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const
export type RecurrenceWeekday = (typeof RECURRENCE_WEEKDAYS)[number]

export const RECURRENCE_ORDINALS = ['first', 'second', 'third', 'fourth', 'last'] as const
export type RecurrenceOrdinal = (typeof RECURRENCE_ORDINALS)[number]

export const RECURRENCE_TRIGGER_TYPES = ['on_completion', 'on_date'] as const
export type RecurrenceTriggerType = (typeof RECURRENCE_TRIGGER_TYPES)[number]

/**
 * Recurrence rule on the wire. Matches the BE's `RecurrenceInputSchema`
 * (see `3mote-be-new/src/schemas/recurrences.ts`). Field-level rules
 * enforced BE-side:
 *   - `frequency === 'weekly'`  → `daysOfWeek.length >= 1` required
 *   - `frequency === 'monthly'` → either `dateOfMonth` (1–31) OR the
 *     `dayInMonth` (WEEKDAY, e.g. 'monday') + `dayOfMonth` (ORDINAL, e.g.
 *     'first') pair, never both. The names are counter-intuitive but
 *     authoritative: they are the stored column names.
 *   - `endDate` and `occurrences` are mutually exclusive (set neither for
 *     "never ends"; set one for a bounded recurrence)
 */
export interface RecurrenceInput {
  frequency: RecurrenceFrequency
  interval: number
  daysOfWeek?: RecurrenceWeekday[]
  dateOfMonth?: number | null
  dayInMonth?: RecurrenceWeekday | null
  dayOfMonth?: RecurrenceOrdinal | null
  endDate?: string | null
  occurrences?: number | null
  triggerType: RecurrenceTriggerType
}

/**
 * Recurrence on a `BoardCard`: the full stored rule (not just the chip
 * fields), so the detail modal can seed its editor from it. Only present on
 * tasks + events; tickets + announcements never carry a recurrence.
 * `updatedAt` changes on every rule edit and is the editor's re-seed key.
 */
export type BoardCardRecurrenceRef = Omit<
  NonNullable<Schemas['BoardCardRecurrence']>,
  'dayInMonth' | 'dayOfMonth'
> & {
  // The generated spec drops `nullable` on these two (they surface as an
  // optional non-null enum), but the wire carries `null` for any rule that
  // isn't a monthly ordinal pattern. Re-declared here with the real shape.
  readonly dayInMonth: RecurrenceWeekday | null
  readonly dayOfMonth: RecurrenceOrdinal | null
}

export type BoardCard = Omit<
  Schemas['BoardCard'],
  // The generated spec drops `nullable` on `repository`; re-declared below.
  | 'repository'
  // `description` is omitted so the Phase 1 rich-text override below can
  // WIDEN it — intersecting the generated required `string` with the
  // dual-format union would silently collapse back to `string`.
  // The scalar reviewer fields are omitted because the BE replaced them
  // with the `reviewers[]` array below — drop them from this list once
  // codegen runs against the multi-reviewer BE and they leave the spec.
  | 'assigneeId'
  | 'assigneeName'
  | 'assigneeAvatarUrl'
  | 'description'
  | 'reviewerId'
  | 'reviewerName'
  | 'reviewerAvatarUrl'
> & {
  /**
   * Wave 4 Batch K — assignee fields are now optional. Present on
   * tasks/tickets, ABSENT on events/announcements (which use `attendees`
   * instead — see below).
   */
  readonly assigneeId?: string | null
  readonly assigneeName?: string | null
  readonly assigneeAvatarUrl?: string | null
  readonly timezone?: string | null
  /**
   * Wave 4 Batch K — events + announcements ship a multi-attendee shape
   * with avatar URLs. Tasks + tickets do not have this field.
   */
  readonly attendees?: readonly BoardCardAttendee[]
  readonly collaborators?: readonly BoardCardUserRef[]
  readonly dates?: readonly AnnouncementDateRow[]
  /**
   * BE shipped `updatedAt` on the BoardCard wire shape — collapse this
   * line to a pure pass-through once `pnpm openapi:codegen` runs.
   */
  readonly updatedAt: string
  /**
   * Rail people section (migration `0024`) — creator info is now on every
   * card on the wire. Collapse these once codegen runs against the
   * deployed BE.
   */
  readonly creatorId: string
  readonly creatorName: string | null
  readonly creatorAvatarUrl: string | null
  /**
   * Rail people section — reviewers are tasks + tickets only (multi-user,
   * same element shape as `attendees`). Surfaced in the UI only when
   * the card's status name is "Review" or "Done" (legacy parity).
   */
  readonly reviewers?: readonly BoardCardAttendee[]
  /**
   * Software version (tickets only — migration `0025`). The FE renders
   * a creatable picker on the ticket modal. Collapse this once codegen
   * picks up the new BoardCard shape.
   */
  readonly versionId?: string | null
  readonly versionName?: string | null
  /**
   * Story point estimate (tasks + tickets only — migration `0026`).
   * Integer >= 1 or null when unset.
   */
  readonly storyPointEstimate?: number | null
  /**
   * Recurrence ref (tasks + events only). `null` when the card has no
   * recurrence rule. Collapse to a one-line pass-through once
   * `pnpm openapi:codegen` runs against the deployed Phase 3 BE.
   */
  readonly recurrence?: BoardCardRecurrenceRef | null
  /**
   * Phase 5 — points back to the card this row was cloned from
   * (recurrence chain). `null` on the original of a chain, on non-recurring
   * cards, and on tickets + announcements (out of scope). The FE resolves
   * the parent's shorthand + title from the in-memory board cache.
   */
  readonly parentId?: string | null
  /**
   * Canonical rich-text Phase 1 bridge (ADR-039). The board payload
   * ships the body inline as `description` on every kind (announcements
   * alias their `body` column onto it — see the BE board route). During
   * the migration window the value is dual-format: legacy markdown
   * STRING or canonical TipTap JSON OBJECT. `descriptionFormat`
   * discriminates (`BoardCardSchema` in `3mote-be-new/src/schemas/board.ts`
   * ships it on every kind, announcements included); typed optional here
   * because pre-Phase-1 BE responses lack it — absent ⇒ markdown. The FE
   * read helper additionally self-discriminates on the value's runtime
   * shape (object ⇒ json) for rollout safety. Collapse once codegen
   * absorbs the Phase 1 BE deploy.
   */
  readonly description?: string | TipTapJsonContent | null
  readonly descriptionFormat?: RichTextFormat
  /**
   * AI agents that are working on this card right now (board, dashboard and
   * single-card payloads). Absent or empty when none. Seeds the glow store;
   * `agent.working` frames keep it current afterwards. Collapse once
   * codegen absorbs the BE field.
   */
  readonly workingAgents?: readonly WorkingAgentRef[]
  /**
   * The one repository this card is about (tasks + tickets only). `null`
   * when unset; absent on events, announcements and chip-mode reads.
   */
  readonly repository?: CardRepository | null
}

/** One agent currently working on a card (`BoardCard.workingAgents[]`). */
export interface WorkingAgentRef {
  readonly agentUserId: string
  readonly agentName: string
  /** ISO timestamp of the agent's first touch in the current run. */
  readonly since: string
}

/**
 * A live agent workstation whose focus is this card — single-card read only
 * (`GET /api/cards/{id}`). Visible to anyone who can read the card; the
 * session controls stay behind `manage-agents`. Absent when no live session
 * has the card as its focus (and on a BE that predates the field).
 */
export interface CardWorkstation {
  readonly agentUserId: string
  readonly sessionId: string
  readonly status: 'launching' | 'running'
  /** ISO timestamp the session ends; the server clock is the truth. */
  readonly expiresAt: string
  readonly size: 'small' | 'medium' | 'large'
}

/**
 * Time on a card, split by who logged it — single-card read only
 * (`GET /api/cards/{id}`). Minutes can be fractional.
 */
export interface CardTimeSummary {
  readonly peopleMinutes: number
  readonly aiMinutes: number
}

/**
 * Estimated model spend on a card — single-card read only, and the key is
 * ABSENT unless the viewer may see cost (`manage-agents`, or the accountable
 * person of an agent that worked on the card). `known: false` means at least
 * one call had no price, so `usd` is a partial figure.
 */
export interface CardAiCost {
  readonly usd: number
  readonly known: boolean
}

/**
 * Unwrapped `/api/boards/:projectId` payload. Same naming reason as
 * `Me` above — `useBoard`'s `queryFn` strips the `{ data: ... }` wrap.
 *
 * Wave 3 Batch J: the `cards` array picks up the optional Batch J
 * fields via the `BoardCard` extension above.
 */
export type BoardResponse = Omit<Schemas['BoardData'], 'cards' | 'project'> & {
  cards: BoardCard[]
  project: Omit<Schemas['BoardData']['project'], 'onlyRepository'> & {
    /** The only linked repository when `repositoryCount` is 1, else `null`. */
    readonly onlyRepository: CardRepository | null
  }
}

// ---------------------------------------------------------------------------
// Phase 2c — comments.
// ---------------------------------------------------------------------------

/**
 * Rich-text wire format discriminator (ADR-039 Phase 1 bridge entry).
 * Ships next to every dual-format body (`descriptionFormat` on
 * tasks/tickets/events, `bodyFormat` on announcements + comments):
 * `'markdown'` = legacy string row, `'json'` = canonical TipTap doc.
 * Collapses to a generated re-export once codegen picks up the Phase 1
 * BE deploy.
 */
export type RichTextFormat = 'markdown' | 'json'

/**
 * `Comment` was widened with `hasReplies` + `replyCount` in Wave 2 Batch F1
 * (now generated). Phase 1 of the canonical rich-text migration (ADR-039)
 * re-opens a bridge override: `body` is dual-format on the wire — a legacy
 * markdown STRING or a TipTap JSON doc OBJECT, discriminated by
 * `bodyFormat` (absent on pre-Phase-1 BE responses ⇒ markdown). Collapse
 * back to a one-line re-export once codegen picks up the Phase 1 BE.
 */
export type Comment = Omit<Schemas['Comment'], 'body'> & {
  body: string | TipTapJsonContent
  readonly bodyFormat?: RichTextFormat
}

/**
 * Per-emoji reaction aggregate on a deliverable comment. Ordered by first
 * use (earliest reaction per emoji leads) — render in the order given, and
 * see `<ReactionChipsBar>` for why re-sorting is wrong. `users` is capped
 * server-side at 12; `count` is the true total.
 */
export type CommentReactionAggregate = Schemas['CommentReactionAggregate']

/** Payload of the two reaction write routes, unwrapped from `{ data: ... }`. */
export type CommentReactionsPayload = Schemas['CommentReactionsPayload']

/**
 * Inner page shape of `GET /api/comments` (unwrapped from `{ data: ... }`).
 * `items` re-points at the Phase 1 dual-format `Comment` above; collapse
 * to a pure re-export with it once codegen absorbs the Phase 1 BE.
 */
export type CommentsListResponse = Omit<Schemas['ListCommentsPayload'], 'items'> & {
  readonly items: readonly Comment[]
}

// ---------------------------------------------------------------------------
// Phase 2d — labels.
// ---------------------------------------------------------------------------

export type Label = Schemas['Label']
export type LabelChip = Schemas['LabelChip']
export type LabelAssignment = Schemas['LabelAssignment']

// ---------------------------------------------------------------------------
// Phase 3b — notifications. Hand-rolled until the next codegen run picks up
// the Phase 3b BE schemas. Once `pnpm openapi:codegen --from=staging` runs
// against the deployed BE these aliases collapse to one-line re-exports.
// ---------------------------------------------------------------------------

/**
 * Notification kinds emitted by the BE. Wave 3 Batch H expanded the set
 * from the original three (mention/assigned) to the legacy-parity 16. The
 * FE renders unknown kinds with a generic line so adding kinds is
 * non-breaking.
 */
export type NotificationKind =
  | 'mention.comment'
  | 'mention.description'
  | 'mention.chat'
  // Phase 4c — wiki body @mention. Triggered BE-side when a wiki save
  // diff-parses a NEW `mention` node added since the prior version. The
  // FE renderer ships the `mentionWikiText` localized line.
  | 'mention.wiki'
  | 'channel.member_joined'
  | 'channel.member_left'
  | 'card.assigned'
  | 'update-deliverable'
  | 'move-deliverable'
  | 'delete-deliverable'
  | 'add-collaborator'
  | 'remove-collaborator'
  | 'unassign-deliverable'
  | 'deliverable-comment'
  // Someone replied to a comment the recipient wrote; same payload as
  // `deliverable-comment`, distinct copy in the bell.
  | 'comment.reply'
  | 'create-event'
  | 'update-event'
  | 'cancel-event'
  | 'create-announcement'
  | 'update-announcement'
  // Wiki doc-level comment notifications. Trigger sites: BE diff-parse
  // on every comment create / update / resolve / reopen.
  | 'wiki_comment.mention'
  | 'wiki_comment.reply'
  | 'wiki_comment.new'
  | 'wiki_comment.resolved'
  // System notice (no actor) — the workspace is over its storage quota
  // during the over-cap grace window. Stamped by the daily reaper for
  // owners + admins; deep-links to Settings → My Organization.
  | 'storage.over_limit'
  // AI agent seats: the agent hit its daily token budget, or a run failed
  // for good. Recipient is the agent's accountable person (or owners/admins
  // when it has none). Deep-links to the agent's Runs.
  | 'agent.budget_exhausted'
  | 'agent.run_failed'

export interface NotificationPayload {
  cardKind?: DeliverableKind
  cardId?: string
  commentId?: string
  /** Top-level comment owning `commentId` when it is a reply; absent otherwise and on older rows. */
  parentCommentId?: string
  actorId?: string
  actorName?: string
  /** Human vs AI agent of the actor; absent on rows stamped before `kind` shipped. */
  actorKind?: UserKind
  /** First ~80 chars of the comment / description context. */
  snippet?: string
  /**
   * Project of the deliverable. Stamped by every Wave 3 trigger so the
   * bell can deep-link to the right board on click.
   */
  projectId?: string | null
  /** Project code prefix (e.g. "MOTE"). Used by the bell title. */
  projectCode?: string | null
  /** Card title at the time of the notification. */
  cardTitle?: string
  /** Card shorthand (e.g. "MOTE-12"). null on announcements. */
  cardShorthand?: string | null
  /** Status column name. Used by the move-deliverable / generic verbs. */
  statusName?: string | null
  /** Used by `update-deliverable` and `update-event` / `update-announcement`. */
  changedFields?: string[]
  /** `move-deliverable`: status names. */
  fromStatus?: string
  toStatus?: string
  /** `unassign-deliverable`. */
  unassignedUserId?: string
  unassignedUserName?: string
  /** `add-collaborator` / `remove-collaborator`. */
  addedUserId?: string
  addedUserName?: string
  removedUserId?: string
  removedUserName?: string
  /** `create-event`. */
  startAt?: string
  endAt?: string | null
  location?: string
  /** `mention.chat` — conversation context stamped by the BE trigger. */
  conversationId?: string
  messageId?: string
  conversationType?: 'channel' | 'dm'
  channelName?: string
  /**
   * `mention.chat` — set to `'all'` when the notification was triggered
   * from an @all expansion. Drives the bell-line
   * variant render: "{actor} mentioned everyone in #channel" instead
   * of the direct-mention "{actor} mentioned you in a message in
   * #channel". Absent for direct @user mentions.
   */
  source?: 'all'
  /**
   * `mention.wiki` — wiki doc context stamped by the Phase 4c BE
   * trigger. `docId` deep-links to `/wiki/d/:docId`; `docTitle` renders
   * inside the notification verb.
   */
  docId?: string
  docTitle?: string
  /**
   * `wiki_comment.*` — comment-thread deep-link payload. `threadId`
   * targets the specific thread in the comments rail (the FE appends
   * `#thread-{threadId}` to the doc URL so `<DocPage>`'s hash
   * observer can open the rail + scroll-to-card on arrival).
   * `commentId` carries the specific comment that triggered the
   * notification (root or reply); reserved for future per-comment
   * deep-links beyond the thread-level scroll.
   */
  threadId?: string
  /**
   * `wiki_comment.*` — discriminator stamped by the BE trigger that
   * identifies which comment surface the notification points at:
   * `'thread'` = rail-style threaded comment, `'page'` =
   * Confluence-style page comment. Drives FE deep-link target:
   * `#thread-{id}` vs `#page-comment-{id}`. Optional for legacy rows
   * minted before the discriminator shipped — absence falls back to
   * `'thread'` for back-compat with the original rail-only flow.
   */
  context?: 'thread' | 'page'
  /**
   * `wiki_comment.resolved` — discriminator for the shared kind: true
   * means a thread was just resolved, false means a previously-resolved
   * thread was reopened. The BE trigger stamps it on every resolve /
   * reopen call.
   */
  resolved?: boolean
  /**
   * `storage.over_limit` — storage accounting at the time the over-cap
   * grace window was detected. `deadline` is the ISO date when the
   * reaper begins removing the oldest files if usage isn't brought back
   * under quota; the bell + banner surface a localized countdown.
   */
  usedBytes?: number
  quotaBytes?: number
  deadline?: string | null
  /** `agent.budget_exhausted` / `agent.run_failed` — the agent's user id, name and run. */
  agentUserId?: string
  agentName?: string
  runId?: string
  /** `agent.run_failed` — why the run failed, when the bell should say more than "failed". */
  reason?: string
}

export interface Notification {
  id: string
  tenantId: string
  recipientId: string
  // BE serializes the field as `kind` (open enum on `notifications.kind`) —
  // keep the FE name aligned. R-1 caught the prior `type` mismatch in 3b.
  kind: NotificationKind
  payload: NotificationPayload
  readAt: string | null
  createdAt: string
}

export interface NotificationsListResponse {
  items: Notification[]
  nextCursor: string | null
  unreadCount: number
}

// ---------------------------------------------------------------------------
// Phase 3c — time tracking. Hand-rolled until the next codegen run picks up
// the Phase 3c BE schemas (one-line aliases like the Phase 2c types above
// once the generated client lands).
// ---------------------------------------------------------------------------

/**
 * Per-entry approval state. Manual entries (`POST /api/time-entries`)
 * land at `'approved'`; timer-stop entries land at `'pending'` and need
 * an explicit approve click (the green check on the calendar entry).
 * Submitting a day's timesheet is gated on every entry being approved.
 */
export type TimeEntryStatus = 'pending' | 'approved'

export interface TimeEntry {
  id: string
  tenantId: string
  userId: string
  /**
   * Untagged entries (no deliverable) carry `null` here, not `undefined`.
   * Detach via `useUpdateTimeEntry` sets these to `null` to match.
   */
  deliverableKind: DeliverableKind | null
  deliverableId: string | null
  projectId: string | null
  /** ISO timestamp. */
  startedAt: string
  /** ISO timestamp; null while the timer is running. */
  endedAt: string | null
  /**
   * Unrounded moment the user actually pressed start / stop (or typed
   * into a manual entry). Org rounding can push `startedAt` into the
   * FUTURE ("round start up to 15 min"), so every live clock counts
   * from `actualStartedAt` and only the timesheet shows the rounded
   * pair. `actualStartedAt` is null only on rows older than the column
   * — fall back to `startedAt`. `actualEndedAt` is null while running
   * and on entries the 8h cap stopped.
   */
  actualStartedAt: string | null
  actualEndedAt: string | null
  notes: string
  snapMinutes: number
  /**
   * `'pending'` for timer-stop entries (user must click the green check
   * on the calendar to approve); `'approved'` for manual entries +
   * approved timer entries. Older BE deploys may omit this field — the
   * FE should treat `undefined` as `'approved'` (least-disruptive
   * default) until the new BE is rolled out.
   */
  status: TimeEntryStatus
  /**
   * Joined status of this entry's day-timesheet. `'pending'` means the
   * day is fully editable; `'submitted'` / `'approved'` mean the entry
   * is read-only (locked) until a manager rejects the submission. The
   * FE uses this to hide edit/delete/approve affordances and render a
   * distinct visual on locked entries.
   */
  timesheetStatus: 'pending' | 'submitted' | 'approved'
  createdAt: string
  updatedAt: string
  /**
   * Phase 3c-2: convenience fields joined from the deliverable when the
   * entry is attached to a card. Both are best-effort — render
   * defensively when absent (the BE may not have deployed yet, or the
   * entry may be untagged). Announcements have no shorthand.
   */
  shorthand: string | null
  deliverableTitle: string | null
}

/** Optional payload fields the BE may attach when starting a timer. */
export interface TimerStartArgs {
  deliverableKind?: DeliverableKind
  deliverableId?: string
  projectId?: string | null
  notes?: string
  snapMinutes?: number
}

/**
 * Reason the BE auto-stopped a running entry. Today the only value the FE
 * surfaces is `'cap'` (8h cap) — every other reason is a normal user-driven
 * stop and renders no toast.
 */
export type TimerStopReason = 'cap' | 'user'

/** Inner page shape of `GET /api/time-entries` (unwrapped from `{ data: ... }`). */
export interface TimeEntriesListResponse {
  items: TimeEntry[]
  nextCursor: string | null
}

/**
 * One bucket of grouped entries. `key` is `YYYY-MM-DD` for `groupBy:'day'`
 * or ISO-week `YYYY-Www` (e.g. `2026-W18`) for `'week'`.
 */
export interface TimesheetGroup {
  key: string
  totalSeconds: number
  entries: TimeEntry[]
}

/** Inner shape of `GET /api/timesheets` (unwrapped from `{ data: ... }`). */
export interface TimesheetResponse {
  groups: TimesheetGroup[]
}

/** Workflow status of a per-day timesheet row. */
export type TimesheetStatus = 'pending' | 'submitted' | 'approved'

/**
 * One day's status row from `GET /api/timesheets/days`. Backs the Submit
 * Timesheets modal — `totalMinutes` drives the hours-worked column, the
 * FE colors `< 480` (8h) in red as a "minimum not met" hint. `status`
 * decides whether the row is interactive (`pending` → checkbox enabled)
 * or read-only (`submitted` / `approved`). `hasPendingEntries` is the
 * client-side hint that submitting this day will be rejected by the BE
 * until every entry is approved.
 */
export interface TimesheetDay {
  date: string
  totalMinutes: number
  status: TimesheetStatus
  submittedAt: string | null
  approvedAt: string | null
  rejectedAt: string | null
  /**
   * Manager's optional reason from the most recent rejection. `null`
   * means never rejected, or rejected without a reason. The Timesheet
   * page's day-header (!) tooltip renders this when shown alongside
   * the "Rejected" badge.
   */
  rejectionReason: string | null
  hasPendingEntries: boolean
  /**
   * The terminal status on this past empty day is a display hint, not a
   * submission — the day still accepts entries. See `lib/day-lock.ts`.
   */
  synthesized?: boolean
}

/** Inner shape of `GET /api/timesheets/days` (unwrapped from `{ data: ... }`). */
export interface TimesheetDaysResponse {
  days: TimesheetDay[]
}

/** Inner shape of `POST /api/timesheets` (unwrapped from `{ data: ... }`). */
export interface SubmitTimesheetsResponse {
  submittedDates: string[]
}

// ---------------------------------------------------------------------------
// Slice C — Manager / Admin Timesheet Approval
// Backs `/approvals` page. Status flow: pending → submitted → approved.
// Rejection is encoded as `status: 'pending' + rejectedAt + rejectionReason`
// per migration 0032 (no separate `rejected` enum value); the queue's
// "rejected" filter selects those rows.
// ---------------------------------------------------------------------------

export type ApprovalQueueStatus = 'submitted' | 'approved' | 'rejected'

export interface TimesheetUserRef {
  id: string
  name: string
  email: string
  avatarUrl: string | null
  kind?: UserKind
}

export interface TimesheetApproverRef {
  id: string
  name: string
}

/**
 * One row in the manager Approval queue: a single user-day timesheet
 * plus the submitter/approver refs + the day's entries inlined for
 * chevron expansion.
 */
export interface ApprovalQueueRow {
  id: string
  tenantId: string
  userId: string
  entriesDate: string
  totalMinutes: number
  status: TimesheetStatus
  submittedAt: string | null
  approvedAt: string | null
  rejectedAt: string | null
  approverId: string | null
  rejectionReason: string | null
  createdAt: string
  updatedAt: string
  submitter: TimesheetUserRef
  approver: TimesheetApproverRef | null
  entries: TimeEntry[]
}

/** Inner shape of `GET /api/timesheets/approval-queue` (unwrapped). */
export interface ApprovalQueuePayload {
  rows: ApprovalQueueRow[]
}

/** Inner shape of `PATCH /api/timesheets/{id}/approve|reject` (unwrapped). */
export interface TimesheetActionPayload {
  timesheet: {
    id: string
    tenantId: string
    userId: string
    entriesDate: string
    totalMinutes: number
    status: TimesheetStatus
    submittedAt: string | null
    approvedAt: string | null
    rejectedAt: string | null
    approverId: string | null
    rejectionReason: string | null
    createdAt: string
    updatedAt: string
  }
}

// ---------------------------------------------------------------------------
// Available Work — backs the right-rail panel on the Timesheet page.
// Cards the caller can log time against right now (legacy parity:
// `GET /board/available-work`). Announcements are excluded.
//
// Each item ships the full `BoardCard` shape (labels + attendees +
// creator + reviewers + priority/severity) so the FE can render with the
// existing `<BoardCard>` component as-is. Three extra fields are layered
// on top: `projectId` + `projectCode` + `projectName` for the lazy
// detail-modal host, and `hasActiveTimer` for the per-card play/stop
// button.
// ---------------------------------------------------------------------------

export type AvailableWorkKind = 'task' | 'ticket' | 'event'

export type AvailableWorkItem = BoardCard & {
  projectId: string
  projectCode: string | null
  projectName: string
  /**
   * The card's column name in its project (a Today, Review or Done
   * column for tasks + tickets; always `Today` for events). The AW panel renders
   * a status pill on each card in place of labels — the panel mixes
   * cards across projects so there's no column header to carry the
   * status visually.
   */
  statusName: string
  hasActiveTimer: boolean
  /**
   * `recentlyDone` — a task or ticket the caller moved to Done recently
   * (within the board's Done-visibility window). People often finish a
   * card before logging its time, so these stay draggable, grouped after
   * the `active` work. The BE sorts every `active` item first.
   */
  section: 'active' | 'recentlyDone'
  /** Whether the card's column lets a timer start (manual entries ignore it). */
  allowsTimer: boolean
}

/** Inner shape of `GET /api/work/available` (unwrapped from `{ data: ... }`). */
export interface AvailableWorkPayload {
  items: AvailableWorkItem[]
  nextPage: number | null
  total: number
}

/** Optional query inputs for `GET /api/work/available`. */
export interface AvailableWorkQuery {
  q?: string
  projectId?: string
  kind?: AvailableWorkKind
  page?: number
  perPage?: number
}

// ---------------------------------------------------------------------------
// Phase 4a — chat (DM-only). Hand-written shim. Collapses to re-exports from
// `src/api/generated/api.ts` once `pnpm openapi:codegen --from=staging`
// includes chat.
// ---------------------------------------------------------------------------

/** Conversation discriminator. Channels ship in Phase 4b. */
export type ChatConversationType = 'dm' | 'channel'

/**
 * Encoding of a chat-message body. `'system'` is reserved for kind='system'.
 * `'json'` is the canonical TipTap ProseMirror encoding (ADR-039 Phase 3)
 * — the `body` field carries the parsed doc OBJECT on those rows, a
 * string on every other format. History rows stay markdown forever.
 */
export type ChatMessageBodyFormat = 'plain' | 'markdown' | 'system' | 'json'

/** Author kind on a chat row. */
export type ChatMessageKind = 'user' | 'system'

/**
 * The other participant on a DM, with the BE's single-`name` field plus
 * an optional `deletedAt` marker (FE renders `(deleted)` when present).
 * Channels (4b) leave this `null` on the conversation summary.
 */
export interface ChatPeer {
  id: string
  name: string
  avatarUrl: string | null
  deletedAt: string | null
  kind?: UserKind
}

/** Author on a decorated message row (BE `messages-service.ts`). */
export interface ChatMessageAuthor {
  id: string
  name: string
  avatarUrl: string | null
  deletedAt: string | null
  kind?: UserKind
}

/**
 * Last-message preview attached to each conversation in the sidebar list.
 * Mirrors `LastMessagePreview` from `src/chat/conversations-service.ts`.
 */
export interface ChatLastMessage {
  id: string
  userId: string
  /**
   * `null` when soft-deleted, system, or undecryptable. FE renders
   * `[message deleted]` then. A TipTap doc OBJECT when
   * `bodyFormat === 'json'` (ADR-039 Phase 3), a string otherwise.
   */
  body: string | TipTapJsonContent | null
  bodyFormat: ChatMessageBodyFormat
  kind: ChatMessageKind
  createdAt: string
}

/**
 * Common-across-types fields shared by DM + channel summaries (Phase 4b).
 * The discriminated union below tags each summary with its `type` so the
 * FE can branch (e.g. render `<ChannelHeader>` vs `<ConversationHeader>`,
 * read `peer.name` vs `name`) without optional-chaining everywhere.
 */
interface ChatConversationCommon {
  id: string
  lastMessage: ChatLastMessage | null
  lastMessageAt: string | null
  /**
   * Per-caller unread count of TOP-LEVEL messages (parent_message_id IS
   * NULL). Replies inside threads are tracked separately on
   * `unreadThreadReplyCount`. The sidebar combined badge sums the two.
   */
  unreadCount: number
  /**
   * Per-caller unread count of REPLIES across every thread in this
   * conversation. A reply is unread if it's strictly newer than the
   * caller's `thread_reads.last_read_reply_id` watermark for its parent
   * (or the caller has no `thread_reads` row for that parent), authored
   * by someone other than the caller, and not soft-deleted. Defaults to
   * `0` on older BE responses.
   */
  unreadThreadReplyCount: number
  lastReadMessageId: string | null
  notificationsMuted: boolean
  createdAt: string
}

/** DM-side of the discriminated union (Phase 4a). */
export interface ChatDmSummary extends ChatConversationCommon {
  type: 'dm'
  /** The other participant. `null` only on transient cache states. */
  peer: ChatPeer | null
}

/**
 * Channel-side of the discriminated union (Phase 4b). The BE returns
 * the channel's name + description + privacy + archive state inline so
 * the sidebar + header render without a second fetch.
 *
 * `members` is populated on the per-conversation `GET /api/conversations/{id}`
 * response (so the header + Settings → Members tab render without a
 * second fetch); the sidebar `GET /api/conversations` list response
 * either omits it or returns an empty array — consumers that depend on
 * the member list should call `useConversation(cid)` directly rather
 * than reading off the sidebar cache.
 */
export interface ChatChannelSummary extends ChatConversationCommon {
  type: 'channel'
  name: string
  description: string | null
  isPrivate: boolean
  isDefault: boolean
  archivedAt: string | null
  /** Channel creator's user id. */
  createdBy: string
  memberCount: number
  /** Whether the caller is a member of the channel. */
  isMember: boolean
  /**
   * Member list, populated on the per-conversation summary route. The
   * sidebar list route may return `undefined` or `[]`. Optional so the
   * sidebar list cache type stays compatible with the per-conversation
   * cache.
   */
  members?: ChatChannelMember[]
}

/**
 * Conversation row as it appears on the sidebar list and the detail
 * route. Phase 4b ships the discriminated union — `type` discriminates
 * DMs (with `peer`) from channels (with `name`, `memberCount`, etc.).
 */
export type ChatConversationSummary = ChatDmSummary | ChatChannelSummary

/**
 * BE wire shape for `GET /api/conversations` (list + single). The BE serializes
 * a unified shape with `peer` and `channel` as nullable nested objects, only
 * one populated per the `type` discriminator. The FE's `ChatConversationSummary`
 * is the flat discriminated-union form every chat component reads against —
 * `normalizeChatConversationSummary` bridges the two so consumers see flat
 * fields (e.g. `conversation.name` on a channel, not `conversation.channel.name`).
 *
 * Once the BE ConversationSummarySchema is unified with ChannelSummarySchema
 * (a wire-shape break), this shim collapses to a pass-through.
 */
interface RawChatConversationSummary {
  id: string
  type: 'dm' | 'channel'
  peer: ChatPeer | null
  channel: {
    name: string
    description: string | null
    isPrivate: boolean
    isDefault: boolean
    archivedAt: string | null
    createdBy: string
    memberCount: number
    members?: ChatChannelMember[]
  } | null
  lastMessage: ChatLastMessage | null
  lastMessageAt: string | null
  unreadCount: number
  /**
   * BE Phase 4d+ field — defaults to `0` if older BE (or transient
   * responses) omits it. The normalize shim coerces below.
   */
  unreadThreadReplyCount?: number
  lastReadMessageId: string | null
  notificationsMuted: boolean
  createdAt: string
}

export function normalizeChatConversationSummary(
  raw: RawChatConversationSummary,
): ChatConversationSummary {
  const common = {
    id: raw.id,
    lastMessage: raw.lastMessage,
    lastMessageAt: raw.lastMessageAt,
    unreadCount: raw.unreadCount,
    unreadThreadReplyCount: raw.unreadThreadReplyCount ?? 0,
    lastReadMessageId: raw.lastReadMessageId,
    notificationsMuted: raw.notificationsMuted,
    createdAt: raw.createdAt,
  } as const

  if (raw.type === 'dm') {
    return { ...common, type: 'dm', peer: raw.peer }
  }

  // Channel branch — `raw.channel` is the populated nested object on the
  // wire. Defensive `?? null` fallbacks cover the rare case the BE sends a
  // null channel on a channel-typed row (shouldn't happen, but we'd rather
  // render `(unnamed)` than crash on a `.name` of `undefined`).
  const ch = raw.channel
  return {
    ...common,
    type: 'channel',
    name: ch?.name ?? '(unnamed)',
    description: ch?.description ?? null,
    isPrivate: ch?.isPrivate ?? false,
    isDefault: ch?.isDefault ?? false,
    archivedAt: ch?.archivedAt ?? null,
    createdBy: ch?.createdBy ?? '',
    memberCount: ch?.memberCount ?? 0,
    // The conversations list + single endpoints only return rows where the
    // caller is a participant (LIST filters to caller's; GET-single asserts
    // participant). `isMember` is always true for these responses.
    isMember: true,
    members: ch?.members,
  }
}

/**
 * One member of a channel — what `<ChannelMembersModal>` renders. The
 * BE returns the join timestamp + a creator flag so the modal can show
 * "Created by Alice on Aug 1" plus per-row remove buttons (hidden on
 * the creator row when the actor isn't the creator).
 */
export interface ChatChannelMember {
  userId: string
  name: string
  avatarUrl: string | null
  kind?: UserKind
  /** ISO timestamp the user joined this channel; `null` for legacy rows. */
  joinedAt: string | null
  /** True iff this row is the channel creator (`conversations.createdBy`). */
  isCreator: boolean
  /** When the user's account is soft-deleted; FE renders `(deleted)` then. */
  deletedAt: string | null
}

/** One row of `GET /api/channels/browse` (channels the caller is NOT a member of). */
export interface ChatBrowsableChannel {
  id: string
  name: string
  description: string | null
  isPrivate: boolean
  memberCount: number
  createdAt: string
}

/** One row of `GET /api/channels/archived` (manage-channels gated). */
export interface ChatArchivedChannel {
  id: string
  name: string
  description: string | null
  isPrivate: boolean
  archivedAt: string
  createdBy: string
  createdAt: string
}

/**
 * Typed payload for system-event messages. Surfaces in 4b+ when channel
 * events fire; Phase 4a never produces a `kind: 'system'` row, but the
 * shape is wired up so the FE can branch when 4b lands.
 */
export type ChatSystemPayload =
  | { event: 'member_joined'; actorId: string; targetUserId: string }
  | { event: 'member_left'; actorId: string; targetUserId: string }
  | {
      event: 'channel_updated'
      actorId: string
      changes: Array<'name' | 'description' | 'is_private' | 'is_default'>
    }
  | { event: 'channel_archived'; actorId: string }
  | { event: 'channel_unarchived'; actorId: string }

/**
 * Phase 4c — reaction summary attached to every persisted message.
 *
 * Wire shape: one entry per distinct emoji on the message, with the
 * total count and the user-id list (so the FE can highlight chips the
 * caller already reacted with). Order is BE-defined (legacy parity:
 * count desc, then first-reaction-time asc).
 */
export interface ChatReactionSummary {
  emoji: string
  count: number
  userIds: string[]
}

/**
 * Phase 4e — thread metadata projection. Set on TOP-LEVEL messages
 * (`parentMessageId === null`) when the message has at least one reply.
 * Reply rows themselves never carry this field.
 *
 * Mirrors `ThreadMetadata` from the BE's `thread-metadata-service.ts`:
 *   - `replyCount` — total non-deleted replies on this thread.
 *   - `lastReplyAt` — ISO timestamp of the most recent reply (`null` only
 *     during a transient state where the count > 0 but the BE projection
 *     hasn't refreshed; treat as "unknown, fall back to count alone").
 *   - `replierUserIds` — up to 5 distinct replier user-ids, recent-first,
 *     for the avatar stack on the thread footer.
 *   - `unreadReplyCount` — per-viewer unread count, derived from
 *     `thread_reads.last_read_reply_id` watermark vs. the chronological
 *     reply list. Zeroed by `POST /api/messages/{id}/thread/read`.
 */
export interface ChatThreadMetadata {
  replyCount: number
  lastReplyAt: string | null
  replierUserIds: string[]
  unreadReplyCount: number
}

/**
 * Chat-message attachment row on the wire. Mirrors
 * `MessageAttachment` from `3mote-be-new/src/chat/messages-service.ts`.
 *
 * `url` is a short-lived (~5 min) presigned GET URL re-minted on every
 * message-list response. Don't cache it in browser storage; re-fetch the
 * message list to get fresh URLs. See `[FE/Chat]` pitfall.
 *
 * This is a temporary hand-written shim until the next
 * `pnpm openapi:codegen --from=local` absorbs the BE's chat attachment
 * extension into the generated client.
 */
export interface MessageAttachment {
  id: string
  filename: string
  contentType: string
  sizeBytes: number
  url: string
}

/**
 * A single message in a conversation timeline. Mirrors `DecoratedMessage`
 * from the BE's `messages-service.ts`.
 *
 * `tenantId` is intentionally absent — the BE wire shape on this surface
 * doesn't include it (timeline rows are always rendered inside a
 * tenant-scoped session) and adding it would cause optimistic-update
 * bookkeeping to drift.
 */
export interface ChatMessage {
  id: string
  conversationId: string
  userId: string
  parentMessageId: string | null
  kind: ChatMessageKind
  /**
   * `null` when soft-deleted, system, or undecryptable. A TipTap doc
   * OBJECT when `bodyFormat === 'json'` (ADR-039 Phase 3 — new writes),
   * a markdown/plain STRING on legacy rows. Both shapes coexist forever;
   * `<MessageRow>` branches on the runtime shape.
   */
  body: string | TipTapJsonContent | null
  bodyFormat: ChatMessageBodyFormat
  /** Phase 4a never returns a system row, but the shape is wired up. */
  systemPayload?: ChatSystemPayload | null
  editedAt: string | null
  deletedAt: string | null
  createdAt: string
  updatedAt: string
  /** `null` when the BE couldn't resolve the author. */
  author: ChatMessageAuthor | null
  /**
   * Phase 4c — emoji reactions. Empty array on freshly-sent messages.
   * Optimistic + WS updates rewrite this list in cache; the BE-returned
   * summary is the source of truth on every settle.
   */
  reactions: ChatReactionSummary[]
  /**
   * Phase 4e — thread projection. Set on top-level messages when
   * `replyCount > 0`; absent / `null` for replies and thread-less
   * top-level rows. Optional so older BE responses (and reply rows)
   * type-check without modification.
   */
  thread?: ChatThreadMetadata | null
  /**
   * File / media attachments on a chat message. Empty
   * array when none. Optional on the wire until the BE deploy lands +
   * `pnpm openapi:codegen` picks up the new `MessageAttachment` schema;
   * consumers should defensively read `message.attachments ?? []`.
   */
  attachments?: MessageAttachment[]
  /**
   * Phase 1 link unfurls — OG metadata preview for the first
   * URL found in the message body. Populated only when the URL
   * resolved to `fetch_status='ok'` in the BE's `link_previews` cache.
   * `null` (or omitted) on every other case: no URL, fetch failed /
   * blocked / timeout, fetch still pending, conversation's
   * `unfurls_enabled=false`, system message. FE renders a
   * `<LinkPreview>` card when set.
   */
  preview?: MessagePreview | null
}

/**
 * Phase 1 — wire shape for a single link preview. Mirrors the
 * BE `ChatLinkPreview` Zod schema in `messages-routes.ts`. All OG fields
 * are nullable since a real-world page may emit any subset.
 */
export interface MessagePreview {
  url: string
  title: string | null
  description: string | null
  ogImageUrl: string | null
  /**
   * Intrinsic px dimensions of `ogImageUrl` — drive hero vs thumbnail
   * layout. Optional so payloads from an older BE (pre-v2 wire shape) or
   * a cache-cold client still type-check.
   */
  imageWidth?: number | null
  imageHeight?: number | null
  siteName: string | null
  ogType: string | null
  /** Content author (e.g. a YouTube channel); set only by provider unfurls. */
  authorName?: string | null
  /**
   * True when the stored proxied image nears its S3 lifecycle expiry —
   * the client fires `POST /api/messages/{id}/preview/fetch` once (see
   * `usePreviewImageRefresh`) so the BE re-stores a fresh copy. Absent
   * on fresh/hotlinked previews and on pre-v3 BE payloads.
   */
  imageRefreshDue?: boolean
}

/** One page of `GET /api/conversations/{id}/messages` (unwrapped). */
export interface ChatMessagesPage {
  messages: ChatMessage[]
  nextCursor: string | null
}

// ---------------------------------------------------------------------------
// Wiki (Phase 1). Hand-written shim mirroring the BE Zod schemas in
// `3mote-be-new/src/schemas/wiki.ts` + the response payloads in
// `3mote-be-new/src/wiki/router.ts`. Collapses to one-line re-exports once
// `pnpm openapi:codegen --from=staging` runs against a BE that ships the
// `WikiNode` / `WikiDocVersion` schemas.
// ---------------------------------------------------------------------------

export type WikiNodeKind = 'folder' | 'doc' | 'file'
export type WikiDefaultTenantAccess = 'none' | 'view' | 'edit'
export type WikiVersionClassification = 'autosave' | 'manual' | 'publish'
export type WikiAclSubjectType = 'user' | 'role' | 'team'
export type WikiAclDocRole = 'viewer' | 'editor' | 'owner'

/**
 * Phase 4 — body storage format. `'md'` is the legacy markdown text shape
 * the FE used pre-Phase-4 (and the format every existing prod doc still
 * carries); `'json'` is the post-cutover TipTap ProseMirror JSON shape
 * the wiki editor now serializes to so slash-only blocks (panels, expand,
 * status, date chips, work-item / video / file embeds) have a lossless
 * home. The doc-meta + version-history wire shapes carry this enum at
 * the top level so the FE can pick its parser without inspecting bytes.
 */
export type WikiBodyFormat = 'md' | 'json'

/**
 * TipTap ProseMirror JSON content shape for wiki bodies. Intentionally
 * loose — the FE never authors this shape by hand; it goes straight from
 * `editor.getJSON()` on save and into `editor.commands.setContent(json)`
 * on load. The structural validation is the editor's schema-walker on
 * setContent (a malformed JSON either parses to fewer nodes or throws);
 * we don't try to mirror the ProseMirror Node interface here because
 * the editor extension set evolves per phase and a tight type would
 * drift behind it.
 */
export type TipTapJsonContent = Record<string, unknown>

/**
 * One row in the wiki tree (folder | doc). Shape mirrors the
 * BE `WikiNodeSchema` + the Phase 2 hierarchical tree extension
 * (`WikiTreeNode` in the BE).
 *
 * Why this is hand-written instead of an alias to the generated
 * `Schemas['WikiTreeNode']`: the BE's recursive zod-openapi schema
 * cannot express its own `children: WikiTreeNode[]` shape — the
 * generated client emits `children: readonly unknown[]`, which would
 * force every consumer to `as WikiNode` cast on every recursion. By
 * declaring the recursive interface here once, downstream tree
 * components (`<WikiTree>`, `<WikiTreeNode>`, `<WikiFavoritesSection>`)
 * get type-safe traversal without per-call casts. The `useWikiTree`
 * hook does the single cast at the network boundary.
 *
 * Collapses to a pure re-export once zod-openapi gains recursive-schema
 * support (tracked upstream).
 */
export interface WikiNode {
  id: string
  tenantId: string
  parentId: string | null
  kind: WikiNodeKind
  name: string
  emoji: string | null
  sortIndex: number
  ownerUserId: string
  /**
   * Vestigial post-Phase-5: bodies live on `draftBody` inline; this
   * column is no longer touched on write. Kept on the wire for FE
   * backward-compat during the deploy window. Always `null` going
   * forward.
   */
  draftVersionId: string | null
  /**
   * Post-Phase-5: holds a `wiki_doc_versions.id` UUID-as-text (was an
   * S3 VersionId pre-pivot). NULL = never published. The FE treats the
   * value as opaque — equality compares against `version.id` from the
   * version-history payload.
   */
  publishedVersionId: string | null
  defaultTenantAccess: WikiDefaultTenantAccess
  inheritsAclFromParent: boolean
  isPhi: boolean
  requirePublishApproval: boolean
  templateId: string | null
  publicShareToken: string | null
  publicShareExpiresAt: string | null
  /**
   * Vestigial post-Phase-5: every JSON-in-DB doc renders through the
   * same TipTap parser. The BE always emits `'json'`; the FE no
   * longer branches on it. Optional in the bridge for graceful
   * degradation; consumers can ignore.
   */
  bodyStorageFormat?: WikiBodyFormat
  /**
   * Post-Phase-5: the live editor state (TipTap ProseMirror JSON). The
   * BE writes to `wiki_nodes.draft_body` on every save; the FE feeds
   * this directly into `editor.commands.setContent(...)`. Empty doc
   * = `{}` JSON object. Optional in the bridge for graceful
   * degradation during the BE→FE deploy window — `undefined` falls
   * back to an empty doc.
   */
  draftBody?: TipTapJsonContent
  /**
   * Post-Phase-5: app-computed plain-text projection of `draftBody`,
   * used as the FTS source. Surfaced on the wire so the FE can preview
   * snippets without re-parsing the JSON. Optional in the bridge for
   * graceful degradation.
   */
  bodyText?: string
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  /**
   * Phase 2 hierarchical-tree extensions. The BE's `WikiTreeNode` (an
   * extension of `WikiNode`) ships these three fields on every row of
   * `GET /api/wiki/tree`. They're optional on the interface so a
   * non-tree-route response (e.g. `POST /api/wiki/docs → { doc }`) can
   * still type-check as a `WikiNode`. Tree consumers should treat
   * absent values as `false` / `[]`.
   */
  isPublished?: boolean
  hasDraftAhead?: boolean
  /**
   * Draft text revision: goes up only when the page text changes (a save
   * that changes it, an action-item tick, a restore). `publishedRevision`
   * is its value at the last publish (null if never published), so a doc
   * has unpublished changes exactly when `draftRevision > publishedRevision`.
   * Optional until codegen absorbs them.
   */
  draftRevision?: number
  publishedRevision?: number | null
  /**
   * Owner identity denormalized onto every tree row so the FE can render
   * a creator byline (e.g. on the Folder Detail page) without a second
   * round-trip per node. Loaded by the BE via a LEFT JOIN against
   * `users` on `owner_user_id`. Optional because non-tree responses
   * (`POST /api/wiki/docs → { doc }`) don't carry them; tree consumers
   * should treat absent values as `null`.
   */
  ownerName?: string | null
  ownerAvatarUrl?: string | null
  children?: WikiNode[]
  /**
   * Phase 5 Wave D — Drive-style file-node metadata. Populated only
   * when `kind === 'file'`; null on `'folder'` and `'doc'`. Mirrors
   * the BE `wiki_nodes.file_*` columns (migration `0050`), with a
   * `CHECK (kind='file' XOR file_s3_key IS NULL)` constraint so the
   * non-null shape is server-guaranteed for file kinds.
   *
   * The FE never reads `fileS3Key` for rendering (the download URL is
   * minted via `GET /api/wiki/files/:id/download`); it's surfaced here
   * for debug + parity. `fileS3VersionId` is `null` between presign and
   * the FE's `POST /attach` call — file rows in that transient state
   * render as "uploading…" placeholders.
   */
  fileS3Key?: string | null
  fileSizeBytes?: number | null
  fileMimeType?: string | null
  fileS3VersionId?: string | null
}

export interface WikiDocVersion {
  id: string
  tenantId: string
  nodeId: string
  /**
   * Vestigial post-Phase-5: S3 is no longer in the body path. The BE
   * still emits the column (empty string for new rows) for FE
   * backward-compat. Don't read this; use `version.id` for equality
   * against `doc.publishedVersionId`.
   */
  s3VersionId: string
  authorUserId: string
  label: string
  note: string | null
  classification: WikiVersionClassification
  contentHashSha256: string
  /**
   * Vestigial post-Phase-5: always `'json'` going forward. Optional
   * in the bridge for graceful degradation.
   */
  bodyStorageFormat?: WikiBodyFormat
  /**
   * Post-Phase-5: the snapshot body (TipTap ProseMirror JSON). Inlined
   * on every `wiki_doc_versions` row; the FE reads this directly
   * instead of fetching from S3. Optional in the bridge for graceful
   * degradation — older list endpoints may omit it.
   */
  body?: TipTapJsonContent
  createdAt: string
}

/** `GET /api/wiki/tree` unwrapped payload. */
export interface WikiTreeResponse {
  favorites: WikiNode[]
  tree: WikiNode[]
}

/** `POST /api/wiki/docs` unwrapped payload. */
export interface WikiCreateDocResponse {
  doc: WikiNode
  presignedPutUrl: string
  presignedPutKey: string
  presignedPutRequiredHeaders: Record<string, string>
  presignedGetUrl: null
}

/** `POST /api/wiki/docs/{id}/duplicate` unwrapped payload. */
export interface WikiDuplicateDocResponse {
  doc: WikiNode
  /**
   * Bytes by which the copied embeds pushed the workspace PAST its plan
   * storage ceiling; `0` when the copy fit outright. Non-zero means the
   * tenant is now over-cap and the daily reaper will start the 90-day
   * grace clock — the FE swaps its success toast for the over-limit
   * warning on this field alone, with no follow-up storage fetch.
   */
  overshootBytes: number
}

/**
 * Advisory wiki-doc edit lock — the holder identity + TTL window the BE
 * stamps on `wiki_nodes`. Hand-typed against the lock-route contract
 * (POST/DELETE `/api/wiki/docs/{id}/lock`); collapse to a generated-type
 * re-export once the next OpenAPI codegen run absorbs the BE branch.
 * `acquiredAt` / `expiresAt` are ISO timestamp strings.
 */
export interface WikiEditLock {
  userId: string
  name: string
  avatarUrl: string | null
  acquiredAt: string
  expiresAt: string
}

/** `GET /api/wiki/docs/{id}` unwrapped payload.
 *
 * Post-Phase-5: bodies live inline on `doc.draftBody`. The legacy
 * `presignedGetUrl` / `presignedGetKey` / `version` / `storageFormat`
 * fields are kept on the wire for backward compat but are always
 * `null` / `'json'` going forward. Consumers should read the body
 * from `doc.draftBody` directly.
 */
export interface WikiGetDocResponse {
  doc: WikiNode
  /**
   * Doc owner identity for the byline ("By {name}"). Loaded by the BE
   * via a JOIN against `users` on `wiki_nodes.owner_user_id` so the
   * cross-author case renders without a second client round-trip.
   * Nullable as defense-in-depth (FK enforces non-null, but the shape
   * keeps the FE resilient if the row ever goes missing).
   */
  owner: {
    id: string
    name: string
    avatarUrl: string | null
  } | null
  /** Vestigial post-Phase-5: always `null`. */
  presignedGetUrl: null
  /** Vestigial post-Phase-5: always `null`. */
  presignedGetKey: null
  /** Vestigial post-Phase-5: always `'json'` going forward. */
  storageFormat?: WikiBodyFormat
  /** Vestigial post-Phase-5: always `null`. */
  version: null
  /**
   * Whether the caller can manage this doc's ACL — BE-computed from
   * `canManageNodeAcl` (Layer-1 `manage-doc-acl` slug OR a Layer-2
   * `doc_role='owner'` grant). The FE's `canManageDocAcl` heuristic
   * reads this so a Layer-2 owner WITHOUT the tenant slug still sees
   * the Share affordance.
   */
  canManageAcl?: boolean
  /**
   * Comments authored by users other than the caller that landed after
   * the caller's last mark-read on this doc. Drives the header Comments
   * button unread badge. Excludes self-authored + soft-deleted rows.
   * Optional in the bridge so the older BE response still type-checks;
   * consumers default to `0` when absent.
   */
  unreadCommentCount?: number
  /**
   * Advisory edit lock currently held on this doc, `null` when free.
   * Lazy-expired server-side — an expired lock serializes as `null`.
   * Optional in the bridge so the pre-lock BE response still
   * type-checks; consumers default to `null` when absent.
   */
  lock?: WikiEditLock | null
}

/** `POST /api/wiki/docs/{id}/save` unwrapped payload.
 *
 * `version` is the newly-inserted `wiki_doc_versions` row for manual
 * saves; `null` for autosave-trigger flushes (which only update the
 * draft pointer under the local-first autosave model — see parent plan
 * §3.2 amendment).
 */
export interface WikiSaveDocResponse {
  doc: WikiNode
  version: WikiDocVersion | null
}

/** `POST /api/wiki/docs/{id}/publish` unwrapped payload. */
export interface WikiPublishDocResponse {
  doc: WikiNode
  version: WikiDocVersion
}

/** `DELETE /api/wiki/docs/{id}` unwrapped payload. */
export interface WikiDeleteDocResponse {
  doc: WikiNode
}

/**
 * `POST /api/wiki/docs/{id}/presign-put` unwrapped payload.
 *
 * `requiredHeaders` MUST be replayed verbatim on the FE PUT — the SSE-KMS
 * headers are part of the AWS signature. Skip one and S3 rejects the PUT
 * with 403 SignatureDoesNotMatch.
 */
export interface WikiPresignPutResponse {
  url: string
  key: string
  requiredHeaders: Record<string, string>
  /**
   * Phase 4 — the body format this signed URL targets. The FE asserts
   * this matches what it asked for in the presign request body
   * (defense against a deploy-window race where the BE drops the field).
   * For pre-Phase-4 callers omitting `format`, this echoes the doc's
   * current `bodyStorageFormat`. Optional in the bridge so the older
   * BE response (no field) still type-checks; consumers default to
   * `'md'` when absent.
   */
  format?: WikiBodyFormat
  expiresAt: string
}

/**
 * Phase 3a — version-history routes.
 *
 * The BE shapes are declared in `3mote-be-new/src/wiki/router.ts` (Phase
 * 3a). Listed here as forward-compat aliases until the next
 * `pnpm openapi:codegen --from=staging` run lands the generated types;
 * after that, collapse to one-line re-exports of
 * `Schemas['WikiListVersionsPayload']`, etc.
 */
export interface WikiListVersionsResponse {
  doc: WikiNode
  versions: WikiDocVersion[]
  nextCursor: string | null
}

export interface WikiPresignVersionResponse {
  doc: WikiNode
  /**
   * Post-Phase-5: the version row carries its body inline on
   * `version.body` (TipTap JSON). The FE reads the body directly
   * instead of fetching from S3.
   */
  version: WikiDocVersion
  /**
   * Vestigial post-Phase-5: no S3 GET URL — body is inline on
   * `version.body`. Kept on the wire for FE backward-compat; always
   * empty string going forward.
   */
  url: string
  /** Vestigial post-Phase-5: always `'json'` going forward. */
  storageFormat?: WikiBodyFormat
  /** Vestigial post-Phase-5: no URL TTL to track. */
  expiresInSeconds: number
}

export type WikiRestoreMode = 'draft' | 'publish'

export interface WikiRestoreVersionResponse {
  doc: WikiNode
  version: WikiDocVersion
  /** True when `mode='publish'` and the published pointer flipped. */
  published: boolean
}

/**
 * Phase 3b — per-doc ACL list payload returned by
 * `GET /api/wiki/docs/{id}/acl`. Mirrors `AclListItem` in
 * `3mote-be-new/src/wiki/acl.ts`.
 *
 * `source='own'` rows carry the real `wiki_acl.id` (modal sends it back
 * on the diff for clarity); `source='inherited'` rows carry `id: null`
 * and point at the ancestor `fromNodeId`. The share modal annotates
 * inherited rows with a "via {parent}" hint + a "Manage on parent"
 * link.
 */
export interface WikiAclListItem {
  id: string | null
  subjectType: WikiAclSubjectType
  subjectId: string
  docRole: WikiAclDocRole
  source: 'own' | 'inherited'
  fromNodeId: string
  grantedAt: string | null
  grantedByUserId: string | null
}

/** `GET /api/wiki/docs/{id}/acl` unwrapped payload. */
export interface WikiAclListResponse {
  doc: WikiNode
  entries: WikiAclListItem[]
  defaultTenantAccess: WikiDefaultTenantAccess
  inheritsFromParent: boolean
}

/** One desired entry sent to `PUT /api/wiki/docs/{id}/acl`. */
export interface WikiAclReplaceEntryInput {
  subjectType: WikiAclSubjectType
  subjectId: string
  docRole: WikiAclDocRole
}

/** `PUT /api/wiki/docs/{id}/acl` request body. */
export interface WikiAclReplaceInput {
  entries: WikiAclReplaceEntryInput[]
}

/** `PUT /api/wiki/docs/{id}/acl` unwrapped payload. */
export interface WikiAclReplaceResponse {
  doc: WikiNode
  /**
   * The full set of own ACL rows after the diff. Inherited entries are
   * NOT included — call `GET /acl` to re-resolve those.
   */
  entries: Array<{
    id: string
    tenantId: string
    nodeId: string
    subjectType: WikiAclSubjectType
    subjectId: string
    docRole: WikiAclDocRole
    grantedByUserId: string
    createdAt: string
  }>
}

/** `PUT /api/wiki/nodes/{id}/access` request body. */
export interface WikiUpdateNodeAccessInput {
  defaultTenantAccess?: WikiDefaultTenantAccess
  inheritsAclFromParent?: boolean
}

/** `PUT /api/wiki/nodes/{id}/access` unwrapped payload. */
export interface WikiUpdateNodeAccessResponse {
  node: WikiNode
}

/**
 * Phase 3.7 WS-3 — `PUT /api/wiki/nodes/{id}/classify`. Hand-written until
 * `pnpm openapi:codegen --from=staging` runs against the deployed BE; mirrors
 * `3mote-be-new` `WikiUpdateNodePhiInput` / `...Payload`.
 */
export interface WikiUpdateNodePhiInput {
  isPhi: boolean
}

/** `PUT /api/wiki/nodes/{id}/classify` unwrapped payload. */
export interface WikiUpdateNodePhiResponse {
  node: WikiNode
}

/**
 * Phase 5 Wave D — Drive-style file-node routes. The four shapes below
 * mirror `3mote-be-new/src/wiki/file-nodes.ts`. Collapses to one-line
 * re-exports once `pnpm openapi:codegen --from=staging` runs against a
 * BE that ships the file-node Zod schemas.
 *
 * Distinct from the inline `wiki-file` slash-menu embed flow at
 * `/api/uploads/presign + /attach` — that one writes an `attachments`
 * row referenced from inside the doc body. Drive-style files write a
 * tree node row directly; the two flows MUST stay separate (different
 * `parentKind`s, different S3 key conventions, different orphan-
 * reaper semantics).
 */
export interface WikiFilePresignInput {
  /** The parent folder id, or `null` to land at root. */
  parentId: string | null
  /** Display name for the node row (typically the original filename). */
  name: string
  /** Original filename — BE uses it to derive the S3 key slug. */
  filename: string
  /**
   * Browser-reported MIME (`file.type`). BE validates against the
   * `wiki-file-node` allowlist (PDF + common images + common docs);
   * mismatches surface as `wiki_file_invalid_mime`.
   */
  contentType: string
  /** Client-reported byte count. BE enforces a 100 MB cap. */
  sizeBytes: number
}

export interface WikiFilePresignResponse {
  /** The freshly-created placeholder `wiki_nodes` row id. */
  nodeId: string
  /** Presigned S3 PUT URL (15-min TTL). */
  uploadUrl: string
  /** Headers that MUST be replayed verbatim on the PUT (SSE-KMS sig). */
  requiredHeaders: Record<string, string>
  /** Canonical S3 key the BE issued. */
  key: string
}

export interface WikiFileAttachInput {
  /** S3 VersionId returned by the PUT response's `x-amz-version-id` header. */
  s3VersionId: string
}

export interface WikiFileAttachResponse {
  /** The now-ready file node (file_s3_version_id populated, updated_at bumped). */
  node: WikiNode
}

export interface WikiFileDownloadResponse {
  /** Short-TTL presigned GET URL — 5 min, or 1h for `video/*`. */
  url: string
  /** TTL in seconds — callers stale-time their cache safely below this. */
  expiresInSeconds: number
}

export interface WikiFileDeleteResponse {
  deleted: true
}

/**
 * Doc-level comment wire shape. Author identity is denormalized onto every
 * row so the thread render doesn't need a separate user lookup. Mirrors
 * `WikiCommentDto` in `3mote-be-new/src/wiki/comments.ts`.
 *
 * The `kind` discriminator separates rail-style anchored threads
 * (`'thread'`) from Confluence-style bottom-of-page comments (`'page'`).
 * Both share this row shape; the list route filters by `kind` so each
 * surface fetches only its own population.
 */
export type WikiCommentKind = 'thread' | 'page'

export interface WikiComment {
  id: string
  tenantId: string
  docId: string
  threadId: string
  parentCommentId: string | null
  authorId: string
  /**
   * Dual-format on the wire (ADR-039 Phase 2): a legacy markdown STRING
   * or a TipTap JSON doc OBJECT, discriminated by `bodyFormat` (absent
   * on pre-Phase-2 BE responses ⇒ markdown).
   */
  body: string | TipTapJsonContent
  readonly bodyFormat?: RichTextFormat
  bodyMentions: string[]
  anchorParagraphId: string | null
  anchorText: string | null
  anchorStart: number | null
  kind: WikiCommentKind
  resolvedAt: string | null
  resolvedByUserId: string | null
  createdAt: string
  updatedAt: string
  author: {
    id: string
    name: string
    avatarUrl: string | null
    kind?: UserKind
  } | null
}

export interface WikiListCommentsResponse {
  comments: WikiComment[]
  nextCursor: string | null
}

export interface WikiCommentResponse {
  comment: WikiComment
}

export interface WikiMarkCommentsReadResponse {
  unreadCommentCount: number
}

/**
 * Aggregated reactor identity surfaced by the BE on every reaction
 * aggregate. Mirrors `WikiDocReactionUserSchema` in the BE wiki schema.
 * `avatarUrl` is the absolute CDN URL (or a presigned legacy fallback)
 * per ADR-011 — render directly into `<img src>`.
 */
export interface WikiReactionUser {
  id: string
  name: string
  avatarUrl: string | null
}

/**
 * Per-emoji aggregate for the doc-level reactions bar. The BE sorts the
 * list by `(count desc, emoji asc)`; `users` is capped server-side at 12
 * (USERS_PER_EMOJI_LIMIT) so the hover popover renders without a tail
 * fetch.
 */
export interface WikiDocReactionAggregate {
  emoji: string
  count: number
  callerReacted: boolean
  users: WikiReactionUser[]
}

export interface WikiDocReactionsResponse {
  reactions: WikiDocReactionAggregate[]
}

/** Per-emoji aggregate for the comment-level reactions bar. Same shape as the doc-level aggregate. */
export interface WikiCommentReactionAggregate {
  emoji: string
  count: number
  callerReacted: boolean
  users: WikiReactionUser[]
}

export interface WikiCommentReactionsResponse {
  reactions: WikiCommentReactionAggregate[]
}

/**
 * Wire payload for `POST /api/wiki/docs/:id/reactions` and
 * `POST /api/wiki/comments/:id/reactions`. The BE returns 201 with this
 * payload on add and 204 (empty body) on remove — callers branch on the
 * response status.
 */
export interface WikiToggleReactionResponse {
  state: 'added' | 'removed'
  emoji: string
}

/**
 * Per-doc activity feed wire shapes. Mirrors `DocActivityRow` /
 * `ListDocActivityResult` in `3mote-be-new/src/wiki/activity.ts` +
 * `WikiDocActivityRowSchema` / `WikiDocActivityPayloadSchema` in
 * `3mote-be-new/src/schemas/wiki.ts`. Collapses to one-line re-exports
 * once `pnpm openapi:codegen --from=staging` runs against the deployed
 * BE.
 */
export type WikiDocActivityAction =
  | 'edit'
  | 'publish'
  | 'restore'
  | 'share'
  | 'unshare'
  | 'move'
  | 'classify'
  | 'view'

export interface WikiDocActivityActor {
  id: string
  name: string
  avatarUrl: string | null
}

export interface WikiDocActivityContext {
  versionLabel?: string
  versionId?: string
  aclSubjectType?: WikiAclSubjectType
  aclSubjectName?: string
  aclDocRole?: WikiAclDocRole
  aclExtraCount?: number
  fromParentName?: string | null
  toParentName?: string | null
  phiState?: boolean
  classifyField?: string
  classifyFrom?: unknown
  classifyTo?: unknown
}

export interface WikiDocActivityRow {
  eventId: string
  kind: 'single' | 'rollup'
  action: WikiDocActivityAction
  actor: WikiDocActivityActor | null
  createdAt: string
  count?: number
  eventIds?: string[]
  context?: WikiDocActivityContext
}

export interface WikiDocActivityPayload {
  events: WikiDocActivityRow[]
  nextCursor: string | null
}

// ---------------------------------------------------------------------------
// Web Push subscriptions (ADR-034). Hand-written shim mirroring the BE Zod
// schemas in `3mote-be-new/src/schemas/push-subscriptions.ts`. The push
// routes (`GET /api/push/vapid-public-key`, `POST` / `DELETE
// /api/me/push-subscriptions`) aren't in the deployed OpenAPI spec yet, so
// `pnpm openapi:codegen --from=staging` doesn't emit them. Collapse each of
// these to a one-line re-export of `Schemas[...]` once the BE deploys and the
// next codegen run picks them up.
// ---------------------------------------------------------------------------

/**
 * Body for `POST /api/me/push-subscriptions`. Mirrors the browser's
 * `PushSubscription.toJSON()` shape (`{ endpoint, keys: { p256dh, auth } }`)
 * so the FE can POST the subscription object near-verbatim, plus optional
 * `userAgent` metadata for the future "your devices" management surface.
 */
export interface SubscribePushInput {
  endpoint: string
  keys: {
    p256dh: string
    auth: string
  }
  userAgent?: string
}

/** Body for `DELETE /api/me/push-subscriptions` — identifies the device row by endpoint. */
export interface UnsubscribePushInput {
  endpoint: string
}

/**
 * Inner payload of `POST /api/me/push-subscriptions` (unwrapped from the BE's
 * `{ data: ... }` envelope). Keys are write-only — never returned.
 */
export interface PushSubscriptionDto {
  id: string
  endpoint: string
  userAgent: string | null
  createdAt: string
  lastSuccessAt: string | null
}

/**
 * Inner payload of `GET /api/push/vapid-public-key` (unwrapped from `{ data:
 * ... }`). The URL-safe base64 VAPID public key used as the
 * `applicationServerKey` at `pushManager.subscribe()` time. Not secret.
 */
export interface VapidPublicKeyDto {
  publicKey: string
}

/* -------------------------------------------------------------------------- */
/*                      List view — GET /api/cards                            */
/* -------------------------------------------------------------------------- */

/**
 * Sortable columns on the list surface. Mirrors `CARD_LIST_SORT_KEYS` in
 * `3mote-be-new/src/schemas/board.ts` — the BE rejects anything else with
 * a `validation_error`, so this union must not drift.
 */
export const CARD_LIST_SORT_KEYS = [
  'created',
  'updated',
  'due',
  'resolved',
  'title',
  'priority',
  'storyPoints',
] as const
export type CardListSortKey = (typeof CARD_LIST_SORT_KEYS)[number]

export type CardListSortOrder = 'asc' | 'desc'

/**
 * A status name, verbatim from `project_statuses.name`.
 *
 * Deliberately NOT a union: projects own their status catalogue (rename,
 * recolour, add, delete), so a card can legitimately sit in any name at
 * all. The BE used to bucket anything outside the canonical five to
 * `Inbox`, which reported a card parked in a custom `Gatekeeping` column
 * as being in Inbox — a wrong status, not just a missing one.
 *
 * The canonical five still exist as the DEFAULT seed for a new project and
 * as the master board's fixed buckets; they are simply no longer the only
 * values this surface can see.
 */
export type CardListColumnName = string

/**
 * One row on the list surface: a full `BoardCard` (so a row can be handed
 * straight to the card detail modal) plus project identity, the canonical
 * column bucket, and the resolved-at stamp.
 *
 * `projectId` / `projectCode` / `projectName` are null TOGETHER for
 * tenant-wide announcements — those belong to no project.
 */
export type CardListItem = BoardCard & {
  readonly projectId: string | null
  readonly projectCode: string | null
  readonly projectName: string | null
  readonly columnName: CardListColumnName
  /**
   * `project_statuses.color` for `columnName` — the same hex the
   * per-project board draws its column-header dot from, so the two
   * surfaces agree even when a project has recoloured its catalogue.
   */
  readonly statusColor: string
  /**
   * When the card finished: `done_at` for tasks + tickets, the end of the
   * time window for events + announcements (and only once it has passed).
   * Null for anything still in flight.
   */
  readonly resolvedAt: string | null
}

/**
 * Status filter option, deduped by NAME across the caller's projects — two
 * projects each having a `Today` is one option that selects both.
 *
 * Name only, no colour: projects can disagree about a shared name's colour,
 * so any single swatch would be true for only some of the rows the option
 * selects. Each ROW carries its own exact colour.
 */
export interface CardListStatusOption {
  readonly name: string
}

/** One status in a project's catalogue, in board order. */
export interface CardListProjectStatus {
  readonly id: string
  readonly name: string
  readonly color: string
}

/**
 * A project's status catalogue. Per-project because status ids are
 * per-project: the same NAME carries a different id in every project, so a
 * row changing status must resolve the target from its own project.
 */
export interface CardListProjectStatuses {
  readonly projectId: string
  readonly statuses: readonly CardListProjectStatus[]
}

/** Filter-roster entry shipped alongside each page. */
export interface CardListAssigneeOption {
  readonly id: string
  readonly name: string
  readonly avatarUrl: string | null
  readonly kind?: UserKind
}

/** Inner payload of `GET /api/cards`, unwrapped from `{ data: ... }`. */
export interface CardListData {
  readonly items: readonly CardListItem[]
  /** Total rows matching the filters, across every page. */
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly meta: {
    readonly assignees: readonly CardListAssigneeOption[]
    readonly statuses: readonly CardListStatusOption[]
    readonly projectStatuses: readonly CardListProjectStatuses[]
  }
}

// ---------------------------------------------------------------------------
// Card activity log.
// ---------------------------------------------------------------------------

// The generated schemas mark nullable members as `| null` on the schema
// itself, so the non-null shapes are recovered with `NonNullable`.
export type ActivityValue = NonNullable<Schemas['ActivityValue']>
export type ActivityActor = NonNullable<Schemas['ActivityActor']>
export type ActivityRevert = Schemas['ActivityRevert']
export type ActivityRevertState = ActivityRevert['state']
export type ActivityRevertReason = NonNullable<ActivityRevert['reason']>
export type CardActivityItem = Schemas['CardActivityItem']
export type CardActivityOp = CardActivityItem['op']
export type CardActivitySource = CardActivityItem['source']
export type ActivityValueType = ActivityValue['type']
export type CardActivityPage = Schemas['CardActivityPage']
export type RevertCardActivityResult = Schemas['RevertCardActivityResponse']['data']

// ---------------------------------------------------------------------------
// AI agent runs and usage.
// ---------------------------------------------------------------------------

/**
 * Cost and duration fields the spec doesn't carry yet. All optional so an older
 * BE (which sends none of them) still type-checks and renders; `null` means
 * "not known" (no price for the model) or, for `durationMs`, "still running".
 */
export type AgentRunSummary = Schemas['AgentRunSummary'] & {
  readonly costUsd?: number | null
  readonly durationMs?: number | null
}
export type AgentRunToolCall = Schemas['AgentRunToolCall']
/**
 * One model call of a run. `step` matches the `step` of the tool calls it
 * issued (null when it can't be read); a retried step has one entry per
 * attempt, in order. `costUsd` is null when the model had no known price.
 */
export interface AgentRunModelCall {
  readonly step: number | null
  readonly attempt: number | null
  readonly model: string
  readonly tokensIn: number
  readonly tokensOut: number
  readonly costUsd: number | null
  readonly createdAt: string
}
export type AgentRunDetail = AgentRunSummary & {
  readonly toolCalls: readonly AgentRunToolCall[]
  readonly modelCalls?: readonly AgentRunModelCall[]
}
export type AgentUsage = Schemas['AgentUsage'] & {
  readonly costTodayUsd?: number
  /** False when some of today's calls used a model with no known price. */
  readonly costTodayComplete?: boolean
}

// ---------------------------------------------------------------------------
// AI agent workstations.
// ---------------------------------------------------------------------------

export type WorkstationSize = 'small' | 'medium' | 'large'
export type WorkstationStatus = 'launching' | 'running' | 'ended' | 'failed'

export interface WorkstationPolicy {
  readonly enabled: boolean
  readonly maxSize: WorkstationSize
  /** Longest single session, 2 to 12 hours. */
  readonly maxHours: number
}

/** The live-session summary carried on each agent in `GET /api/agents` (optional until the BE ships it). */
export interface AgentWorkstationBadge {
  readonly status: 'launching' | 'running'
  readonly expiresAt: string
}

/** Payload of the tenant-wide `agent.workstation.changed` WebSocket frame. */
export interface AgentWorkstationChangedPayload {
  readonly agentUserId: string
  readonly sessionId?: string
  readonly status?: WorkstationStatus
  readonly expiresAt?: string
  /** The card the session is focused on, when it has one. */
  readonly focusCard?: { readonly kind: string; readonly id: string } | null
}

export interface WorkstationSession {
  readonly id: string
  readonly agentUserId: string
  /** Who started it; resolve the name from the tenant users list. */
  readonly startedByUserId: string | null
  /** The person the session works for (the requester behind an agent-started session). */
  readonly actingForUserId?: string | null
  /** The brief the agent started the session with; null for a session a person started. */
  readonly task?: string | null
  readonly runId: string | null
  readonly instanceId: string | null
  readonly publicIp: string | null
  /** The actual machine type, e.g. `t4g.large`. */
  readonly instanceType: string | null
  readonly size: WorkstationSize
  readonly status: WorkstationStatus
  /**
   * Why it ended: finished | expired | stopped | terminated | agent_deactivated |
   * launch_failed | interrupted_unrecoverable. An open string so a newer BE reason degrades to its raw text.
   */
  readonly stopReason: string | null
  readonly requestedHours: number
  /** How many Spot interruptions the session was moved through; absent on an older BE. */
  readonly interruptions?: number
  readonly launchedAt: string | null
  readonly expiresAt: string
  readonly endedAt: string | null
  readonly createdAt: string
}

export interface AgentWorkstation {
  readonly policy: WorkstationPolicy
  /** The live (launching or running) session, if any. */
  readonly session: WorkstationSession | null
  /** Decimal hours of workstation time used this month. */
  readonly hoursUsed: number
  readonly hoursCap: number
  /** Up to the last 20 sessions, newest first. */
  readonly recent: readonly WorkstationSession[]
}

export interface UpdateWorkstationPolicyInput {
  readonly enabled?: boolean
  readonly maxSize?: WorkstationSize
  readonly maxHours?: number
}
export interface StartWorkstationInput {
  readonly hours: number
  readonly size?: WorkstationSize
}

export type AgentRunStatus = AgentRunSummary['status']
export type AgentRunTriggerSource = NonNullable<Schemas['AgentInboxSource']>
// The generated `trigger.source` carries a stray `& Record<string, never>` (from the
// BE's catchall) that turns every member into `never` for anything built by hand;
// restate it as the plain source shape.
export type AgentRunTrigger = Omit<AgentRunSummary['trigger'], 'source'> & {
  source: AgentRunTriggerSource
}
export type AgentRunsPage = Omit<Schemas['ListAgentRunsResponse'], 'data'> & {
  readonly data: readonly AgentRunSummary[]
}

/** USD per 1M tokens. `cacheWrite` is null for providers that don't price it separately. */
export interface AgentPriceRates {
  readonly input: number
  readonly cacheWrite: number | null
  readonly cacheHit: number
  readonly output: number
}

/** Weekdays 1 (Monday) to 7 (Sunday) and UTC clock times ("01:00"). */
export interface AgentPricePeakWindow {
  readonly days: readonly number[]
  readonly from: string
  readonly to: string
}

export interface AgentPrice {
  /** `none` = no catalog row and no custom price, so costs are not known. */
  readonly source: 'catalog' | 'override' | 'none'
  readonly effectiveFrom: string | null
  readonly rates: AgentPriceRates | null
  readonly offPeakRates: AgentPriceRates | null
  readonly peakWindowsUtc: readonly AgentPricePeakWindow[] | null
  readonly sourceUrl: string | null
}

/** Body of `PUT /api/agents/{id}/price`, in USD per 1M tokens. */
export interface SetAgentPriceInput {
  input: number
  cacheHit: number
  output: number
  cacheWrite?: number
}

/** Payload of the `agent.turn.started` / `agent.turn.finished` WebSocket frames (not in the OpenAPI spec). */
export interface AgentTurnFramePayload {
  agentUserId: string
  runId: string
  conversationId: string | null
  parentMessageId: string | null
  cardKind: string | null
  cardId: string | null
  projectId: string | null
}

// ---------------------------------------------------------------------------
// Reports — by-model report (hand-written until codegen carries it).
// ---------------------------------------------------------------------------

/** One time bucket of the report window. `byModel[].buckets[].key` joins on `key`. */
export interface ModelReportBucket {
  readonly key: string
  readonly start: string
}

export interface ModelReportModelBucket {
  readonly key: string
  readonly items: number
  readonly points: number
  readonly costUsd: number
}

export interface ModelReportModel {
  readonly provider: string | null
  readonly model: string
  readonly items: number
  readonly points: number
  readonly aiMinutes: number
  readonly costUsd: number
  readonly costKnown: boolean
  /** ISO instant of the first credited card inside the window. */
  readonly firstSeen: string
  /**
   * ISO instant of the model's first credit EVER, which may precede the
   * window. A model is "new" only when this falls inside it.
   */
  readonly firstSeenEver: string
  readonly buckets: readonly ModelReportModelBucket[]
}

/** The report window as the by-model endpoint echoes it (no bucket list; `buckets` is top-level). */
export type ModelReportWindow = Omit<Schemas['ReportWindow'], 'buckets'>

export interface ModelReport {
  readonly window: ModelReportWindow
  readonly buckets: readonly ModelReportBucket[]
  readonly byModel: readonly ModelReportModel[]
}

/** `GET /api/reports/models` — the `{ data: ... }` envelope. */
export interface ModelReportResponse {
  readonly data: ModelReport
}

/**
 * One row of `GET /api/agent-models`: the model an AI agent runs on, so every
 * surface that names an agent can say which model it is. Hand-written until
 * the route lands in the generated OpenAPI types.
 */
export interface AgentModelRow {
  readonly userId: string
  readonly model: string
}

// ---------------------------------------------------------------------------
// MOTE-79 A4a — code connections, project repositories, card repository.
// ---------------------------------------------------------------------------

export type CodeProvider = Schemas['CodeProvider']
export type CodeConnectionStatus = Schemas['CodeConnectionStatus']
/** Why a connection is not `active`; the generated enum also lists `null`. */
export type CodeStatusReason = NonNullable<Schemas['CodeStatusReason']>
export type CodeConnection = Schemas['CodeConnection']
export type CodeConnectionLimit = Schemas['CodeConnectionLimit']
export type CodeRepository = Schemas['CodeRepository']
export type ProjectRepository = Schemas['ProjectRepository']
/** The repository a task or ticket is about, as the board payload carries it. */
export type CardRepository = NonNullable<Schemas['CardRepository']>
export type CodeConnectionsList = Schemas['ListCodeConnectionsResponse']['data']
export type CodecommitSetup = Schemas['CodecommitSetupResponse']['data']
/** `truncated` = the host's repository list was cut short, so the count may be incomplete (nothing was removed). */
export type CodeConnectionCheckResult = Schemas['CheckCodeConnectionResponse']['data']
export type CodeConnectionRefreshResult = Schemas['RefreshCodeConnectionResponse']['data']
/** `GET /api/agents/{id}/code-access`: where an agent can work, from its own memberships. */
export type AgentCodeAccess = Schemas['AgentCodeAccessResponse']['data']
export type AgentCodeAccessProject = AgentCodeAccess['projects'][number]
export type CodeConnectionUsage = Schemas['CodeConnectionUsageResponse']['data']
export type CodeConnectionDisconnectResult = Schemas['DisconnectCodeConnectionResponse']['data']
export type GithubFinishInput = Schemas['FinishGithubConnectInput']
export type GithubFinishResult = Schemas['FinishGithubConnectResponse']['data']
